/* eslint-disable @typescript-eslint/no-non-null-assertion */
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, Signal } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule, DialogModule, DialogService, TabsModule } from '@sag-ui';
import { select, Store } from '@ngxs/store';
import { catchError, EMPTY, filter, map, Observable, ReplaySubject, takeUntil, tap } from 'rxjs';
import {
    LoadCodById,
    LoadCreditReview,
    LoadFinanceReview,
    LoadInvestigationReviewDomestic,
    LoadInvestigationReviewInternational,
    LoadManagerReview,
    ReassignCod,
    ResetCodReview
} from '../../state/cod-review.action';
import { CodReviewState } from '../../state/cod-review.state';
import { CodDetailLoadingCardComponent } from '../cod-detail-loading-card/cod-detail-loading-card.component';
import { CodInvestigationReviewInternationalStepComponent } from '../review-steps/investigation-review-international/investigation-review-international.component';
import { CodManagerReviewStepComponent } from '../review-steps/manager-review/manager-review.component';
import { CodFinanceReviewStepComponent } from '../review-steps/finance-review/finance-review.component';
import { CodCreateFormGroup } from '../../models/cod-create.model';
import { CodCreateOperations } from '../../operations/cod-create.operations';
import { CodCreateFormComponent } from '../cod-create-form/cod-create-form.component';
import { NgxsFormDirective } from '@ngxs/form-plugin';
import { StepConfig } from '../../../shared/types/step-config.type';
import { CodDetailCardComponent } from '../cod-detail-card/cod-detail-card.component';
import { CodStatus, IUser, Region, Roles } from '@cod-monorepo/interfaces';
import {
    CreditReviewForm,
    FinanceReviewForm,
    InvestigationReviewDomesticForm,
    InvestigationReviewInternationalForm,
    ManagerReviewForm
} from '../../models/cod-review.model';
import { CodCreditReviewStepComponent } from '../review-steps/credit-review/credit-review.component';
import { CodInvestigationReviewDomesticStepComponent } from '../review-steps/investigation-review-domestic/investigation-review-domestic.component';
import { GeneralCod } from '../../models/cod.model';
import { ReassignCodDialogComponent } from '../dialogs/reassign-cod/reassign-cod.component';
import { statusRoleMapping } from './cod-review.config';
import { UserState } from '../../../user-management/state/user-management.state';
import { CodCommentsComponent } from '../cod-comments/cod-comments.component';

@Component({
    selector: 'app-cod-review',
    templateUrl: './cod-review.component.html',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ButtonModule,
        DialogModule,
        RouterModule,
        TabsModule,
        NgxsFormDirective,
        CodDetailCardComponent,
        CodDetailLoadingCardComponent,
        CodInvestigationReviewInternationalStepComponent,
        CodManagerReviewStepComponent,
        CodFinanceReviewStepComponent,
        CodCreateFormComponent,
        CodCreditReviewStepComponent,
        CodInvestigationReviewDomesticStepComponent,
        CodCommentsComponent
    ],
    standalone: true
})
export class CodReviewComponent implements OnInit {
    protected selectedCod: Signal<GeneralCod | null> = select(CodReviewState.selectedCod);
    protected cod$: Observable<GeneralCod | null> = inject(Store).select(CodReviewState.selectedCod);
    protected loading: Signal<boolean> = select(CodReviewState.loading);
    protected accessDenied: Signal<boolean> = select(CodReviewState.accessDenied);
    public steps: Signal<StepConfig[]> = select(CodReviewState.steps);
    public user: Signal<IUser | undefined> = select(UserState.user);

    protected creditReview: Signal<CreditReviewForm | undefined> = select(CodReviewState.creditReviewFormModel);
    protected managerReview: Signal<ManagerReviewForm | undefined> = select(CodReviewState.managerReviewFormModel);
    protected financeReview: Signal<FinanceReviewForm | undefined> = select(CodReviewState.financeReviewFormModel);
    protected investigationReviewInt: Signal<InvestigationReviewInternationalForm | undefined> = select(
        CodReviewState.investigationReviewIntFormModel
    );
    protected investigationReviewDomestic: Signal<InvestigationReviewDomesticForm | undefined> = select(
        CodReviewState.investigationReviewDomesticFormModel
    );

    public formGroup: FormGroup<CodCreateFormGroup> | null = null;
    public regions = Region;
    public codStatus = CodStatus;
    public canReassign = false;
    public canEditManagerReview = false;
    public canEditFinanceReview = false;
    public canEditCreditReview = false;
    public canEditInvestigationReview = false;
    public readonly printDate = new Date().toLocaleDateString();
    protected isPrinting = false;

    private destroy$ = new ReplaySubject<void>(1);

    constructor(
        private activeRoute: ActivatedRoute,
        private store: Store,
        private dialogService: DialogService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.initializeRouteIdChangeListener();
        this.initCodChangeListener();
        this.initializeForm();
    }

    protected initializeForm(): void {
        this.formGroup = CodCreateOperations.initializeForm();
    }

    protected cancelCreation(): void {
        this.store.dispatch(new ResetCodReview());
        this.router.navigate(['/']);
    }

    protected async printCod(): Promise<void> {
        const element = document.getElementById('cod-print-section');
        if (!element || this.isPrinting) return;

        this.isPrinting = true;
        element.style.width = '794px';
        element.style.display = 'block';

        try {
            const reportWidth = element.offsetWidth;
            const reportHeight = element.scrollHeight;
            // use scale 2 when the canvas fits; drop to 1 above ~14 pages to stay under Chrome's 32 767-px canvas limit
            const scale = reportHeight * 2 <= 30000 ? 2 : 1;
            // minimum windowHeight that contains the element in the virtual document (scrollY compensation applied)
            const windowHeight = Math.ceil(element.getBoundingClientRect().top + window.scrollY + reportHeight);

            const canvas = await html2canvas(element, {
                backgroundColor: '#ffffff',
                logging: false,
                scale,
                scrollX: -window.scrollX,
                scrollY: -window.scrollY,
                useCORS: true,
                windowWidth: reportWidth,
                windowHeight,
                // onclone guarantees the element is visible in the cloned document regardless of CSS specificity
                onclone: (clonedDoc: Document) => {
                    const el = clonedDoc.getElementById('cod-print-section');
                    if (el) {
                        el.style.display = 'block';
                        el.style.width = `${reportWidth}px`;
                    }
                }
            });

            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const captureScale = canvas.width / reportWidth;
            const pageCanvasHeight = Math.floor((canvas.width * pageHeight) / pageWidth);

            const reportTop = element.getBoundingClientRect().top;
            const orderLineBounds = Array.from(element.querySelectorAll<HTMLElement>('.print-order-line')).map(
                orderLine => {
                    const b = orderLine.getBoundingClientRect();
                    return {
                        top: Math.round((b.top - reportTop) * captureScale),
                        bottom: Math.round((b.bottom - reportTop) * captureScale)
                    };
                }
            );

            let sourceTop = 0;
            let isFirstPage = true;

            while (sourceTop < canvas.height) {
                let sourceBottom = Math.min(sourceTop + pageCanvasHeight, canvas.height);
                const splitOrderLine = orderLineBounds.find(
                    l =>
                        l.top > sourceTop &&
                        l.top < sourceBottom &&
                        l.bottom > sourceBottom &&
                        l.bottom - l.top <= pageCanvasHeight
                );

                if (splitOrderLine) {
                    sourceBottom = splitOrderLine.top;
                }

                if (sourceBottom <= sourceTop) {
                    sourceBottom = Math.min(sourceTop + pageCanvasHeight, canvas.height);
                }

                const pageCanvas = document.createElement('canvas');
                pageCanvas.width = canvas.width;
                pageCanvas.height = sourceBottom - sourceTop;
                pageCanvas
                    .getContext('2d')
                    ?.drawImage(
                        canvas,
                        0,
                        sourceTop,
                        canvas.width,
                        pageCanvas.height,
                        0,
                        0,
                        canvas.width,
                        pageCanvas.height
                    );

                if (!isFirstPage) {
                    pdf.addPage();
                }

                pdf.addImage(
                    pageCanvas.toDataURL('image/png'),
                    'PNG',
                    0,
                    0,
                    pageWidth,
                    (pageCanvas.height * pageWidth) / canvas.width
                );
                sourceTop = sourceBottom;
                isFirstPage = false;
            }

            const cod = this.selectedCod();
            pdf.save(`COD-${cod?.serial ?? 'report'}.pdf`);
        } finally {
            element.style.display = 'none';
            element.style.width = '';
            this.isPrinting = false;
        }
    }

    protected reassignCod(): void {
        const status = this.selectedCod()?.status as CodStatus;
        const dialogRef = this.dialogService.open<string, { role: Roles }>(ReassignCodDialogComponent, {
            data: { role: statusRoleMapping[status] },
            width: '500px'
        });

        dialogRef.afterClosed
            .pipe(
                tap(result => {
                    const id = this.selectedCod()?.id;

                    if (result && id) {
                        this.store.dispatch(new ReassignCod(id, result));
                    }
                })
            )
            .subscribe();
    }

    private initializeRouteIdChangeListener(): void {
        this.activeRoute.params
            .pipe(
                takeUntil(this.destroy$),
                filter(params => !!params['id']),
                map(params => params['id'] as string),
                tap((id: string) => this.store.dispatch(new LoadCodById(id))),
                catchError(error => {
                    console.error(error);
                    return EMPTY;
                })
            )
            .subscribe();
    }

    private initCodChangeListener(): void {
        this.cod$
            .pipe(
                takeUntil(this.destroy$),
                filter(cod => !!cod),
                tap(cod => {
                    const user = this.user();
                    this.canReassign =
                        !!user &&
                        (cod.assignedTo === user.gid ||
                            user.roles.some(role => [Roles.ADMIN, Roles.MANAGER].includes(role)));

                    if (cod.region === Region.NAM) {
                        this.store.dispatch(new LoadManagerReview(cod.id!));
                        this.store.dispatch(new LoadFinanceReview(cod.id!));
                        this.store.dispatch(new LoadInvestigationReviewDomestic(cod.id!));
                    } else {
                        this.store.dispatch(new LoadCreditReview(cod.id!));
                        this.store.dispatch(new LoadInvestigationReviewInternational(cod.id!));
                    }

                    this.canEditManagerReview =
                        !!user &&
                        cod.status === CodStatus.MANAGER_REVIEW &&
                        cod.assignedTo === user.gid &&
                        user.roles.includes(Roles.MANAGER);

                    this.canEditFinanceReview =
                        !!user &&
                        cod.status === CodStatus.FINANCE_REVIEW &&
                        cod.assignedTo === user.gid &&
                        user.roles.includes(Roles.FINANCE_MANAGER);

                    this.canEditCreditReview =
                        !!user &&
                        cod.status === CodStatus.CREDIT_REVIEW &&
                        cod.assignedTo === user.gid &&
                        user.roles.includes(Roles.CREDIT_REVIEWER);

                    this.canEditInvestigationReview =
                        !!user &&
                        cod.status === CodStatus.INVESTIGATION &&
                        cod.assignedTo === user.gid &&
                        user.roles.includes(Roles.INVESTIGATOR);
                })
            )
            .subscribe();
    }
}
